import { Features } from "@app/shared/active_feature_flag_collection";
import { AppointmentAnalyticsService } from "./appointment-analytics.service";
import { AppointmentBookingState } from "./appointment-booking-state";
import { AppointmentService } from "./appointment.service";
import { BehaviorSubject, ObservableInput, Observable } from "rxjs";
import { FeatureFlagService } from "@app/shared/feature-flag.service";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Title } from "@angular/platform-browser";
import { take, map } from "rxjs/operators";
import { UserService } from "../core/user.service";
import { ApiService } from "../core/api.service";
import { AppointmentType } from "./appointment-type";

export const APPOINTMENT_BOOKING_START_ROUTE = "/appointments/new";
//
//  Appointment Booking Steps (could be moved out of here)
//
export interface AppointmentBookingStep {
  route: () => string;
  isReadyToLoad: () => boolean;
  next: () => AppointmentBookingStep;
  previous: () => AppointmentBookingStep;
  percentComplete: number;
}

export class AppointmentBookingSteps {
  private state: AppointmentBookingState;
  constructor() {}

  setAppointmentBookingState(state: AppointmentBookingState) {
    this.state = state;
  }

  initialStep() {
    return this.ReasonForVisit;
  }

  ReasonForVisit: AppointmentBookingStep = {
    route: () => APPOINTMENT_BOOKING_START_ROUTE,
    isReadyToLoad: () => true,
    next: () => {
      if (this.state.requiredSurveyId) return this.Survey;
      if (this.state.getSelectedProviderId() && this.state.providerTypeSelected()) {
        return this.SelectProvider.next();
      }
      return this.SelectProvider;
    },
    previous: () => undefined,
    percentComplete: 0,
  };

  SelectProvider: AppointmentBookingStep = {
    route: () => "/appointments/select-provider",
    isReadyToLoad: () => this.state.hasReasons(),
    next: () =>
      this.state.bookingStateComplete() && this.state.getIsAppointmentRecommended()
        ? this.Search
        : this.SelectType,
    previous: () => this.ReasonForVisit,
    percentComplete: 33,
  };

  SelectType: AppointmentBookingStep = {
    route: () => "/appointments/select-type",
    isReadyToLoad: () =>
      this.state.hasReasons() && this.state.serviceAreaSet() && this.state.providerTypeSelected(),
    next: () =>
      !!this.state.requiredSurveyId && !this.state.surveyData ? this.Survey : this.Search,
    previous: () => this.SelectProvider,
    percentComplete: 57,
  };

  Survey: AppointmentBookingStep = {
    route: () => `/appointments/survey/${this.state.requiredSurveyId}`,
    isReadyToLoad: () =>
      !!this.state.requiredSurveyId && this.state.hasReasons() && this.state.serviceAreaSet(),
    next: () => this.Search,
    previous: () =>
      this.state.getIsAppointmentRecommended() ? this.ReasonForVisit : this.SelectType,
    percentComplete: 75,
  };

  Search: AppointmentBookingStep = {
    route: () => "/appointments/search",
    isReadyToLoad: () => this.state.bookingStateComplete(),
    next: () => null,
    previous: () => null,
    percentComplete: 100,
  };
}

//
//  end Appointment Booking Steps
//

@Injectable()
export class AppointmentBookingStateService {
  currentStep: AppointmentBookingStep;
  private _percentComplete$ = new BehaviorSubject<number>(0);
  percentComplete$ = this._percentComplete$.asObservable();

  private appointmentBookingState: AppointmentBookingState;
  private appointmentBookingSteps: AppointmentBookingSteps;
  private searchResultsFeatureActive: boolean;

  constructor(
    private apiService: ApiService,
    private appointmentAnalyticsService: AppointmentAnalyticsService,
    private appointmentService: AppointmentService,
    private featureFlagService: FeatureFlagService,
    private router: Router,
    private title: Title,
    private userService: UserService
  ) {
    this.appointmentBookingSteps = new AppointmentBookingSteps();
    this.title.setTitle("Appointment Booking");
    this.subscribeToSearchResultsFeatureActive();
    this.setAppointmentBookingState(new AppointmentBookingState());
    this.currentStep = this.appointmentBookingSteps.initialStep();
    this.updateBookingStateOnUserServiceAreaChange();
  }

  setAppointmentBookingState(appointmentBookingState: AppointmentBookingState) {
    this.appointmentBookingState = appointmentBookingState;
    if (!appointmentBookingState.serviceAreaSet()) {
      this.userService.user$.pipe(take(1)).subscribe(user => {
        this.appointmentBookingState.setSelectedServiceArea(user.serviceArea);
      });
    }
    this.appointmentAnalyticsService.setBookingState(appointmentBookingState);
    this.appointmentBookingSteps.setAppointmentBookingState(appointmentBookingState);
  }

  getAppointmentBookingState() {
    return this.appointmentBookingState;
  }

  getAppointmentBookingSteps(): AppointmentBookingSteps {
    return this.appointmentBookingSteps;
  }

  advance() {
    const nextStep = this.currentStep.next();

    // feature flag to be deprecated
    if (nextStep.percentComplete === 100) {
      this._percentComplete$.next(nextStep.percentComplete);
      if (this.searchResultsFeatureActive) {
        return this.appointmentService
          .saveBookingState(this.appointmentBookingState)
          .subscribe(() => {
            this.loadStep(nextStep);
          });
      } else {
        return this.appointmentService.navigateToMyOneSearch(this.appointmentBookingState);
      }
    }
    this.loadStep(nextStep);
  }

  backtrack() {
    if (this.backtrackAvailable()) {
      this.loadStep(this.currentStep.previous());
    } else {
      throw new Error("Cannot backtrack flow!");
    }
  }

  advanceAvailable(): boolean {
    return this.currentStep.next().isReadyToLoad();
  }

  backtrackAvailable(): boolean {
    return !!this.currentStep.previous();
  }

  loadStep(step: AppointmentBookingStep, navigateToStep: boolean = true): AppointmentBookingStep {
    if (!step.isReadyToLoad()) {
      this.loadStep(this.appointmentBookingSteps.initialStep());
      const state = this.appointmentBookingState;
      throw new Error(
        `AppointmentBookingState not ready to advance. Requirements - ${
          step.isReadyToLoad
        } | BookingState 
        hasReasons: ${state.hasReasons()}; 
        providerTypeSelected: ${state.providerTypeSelected()}; 
        serviceAreaSet: ${state.serviceAreaSet()}; 
        appointmentTypeSelected: ${state.appointmentTypeSelected()}`
      );
    }
    this.currentStep = step;
    this._percentComplete$.next(this.currentStep.percentComplete);
    const route = step.route();
    //
    if (!this.router.isActive(route, true) && navigateToStep) {
      this.router.navigate([route]);
    }
    return step;
  }

  fetchRecommendedAppointmentType(): Observable<AppointmentType> {
    const reason = this.appointmentBookingState.selectedReasonsString();
    const serviceArea = this.appointmentBookingState.getSelectedServiceArea();
    const apiParams = {
      reason,
      service_area_id: serviceArea && serviceArea.id,
    };

    const request = this.apiService
      .post("/api/v2/patient/quickumls/get_appointment_type_by_reason", apiParams, false)
      .pipe(
        map(resp => resp["appointment_type"] && AppointmentType.fromApiV2(resp["appointment_type"]))
      );

    request.subscribe((appointmentType: AppointmentType | undefined) => {
      this.appointmentBookingState.updateRecommendedAppointmentType(appointmentType);
    });

    return request;
  }

  private updateBookingStateOnUserServiceAreaChange() {
    this.userService.user$.subscribe(user => {
      this.appointmentBookingState.setSelectedServiceArea(user.serviceArea);
    });
  }

  private subscribeToSearchResultsFeatureActive() {
    this.featureFlagService
      .featureActive$(Features.SEARCH_RESULTS)
      .subscribe(filterActive => (this.searchResultsFeatureActive = filterActive));
  }
}
